/**
 * Workshop ChartFrame — Tremor-style minimal bar trend.
 *
 * Props-in, JSX-out. Local selection state only.
 */

import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

interface ChartPoint {
  label: string;
  value: number;
}

interface ChartFrameProps {
  title: string;
  insight?: string;
  points?: ChartPoint[];
  unit?: string;
  status?: FrameStatus;
  className?: string;
  "data-testid"?: string;
}

const MOCK_POINTS: ChartPoint[] = [
  { label: "Sep 5", value: 189 },
  { label: "Sep 6", value: 204 },
  { label: "Sep 7", value: 176 },
  { label: "Sep 8", value: 198 },
  { label: "Sep 9", value: 221 },
  { label: "Sep 10", value: 207 },
  { label: "Sep 11", value: 168 },
  { label: "Sep 12", value: 152 },
  { label: "Sep 13", value: 164 },
  { label: "Sep 14", value: 183 },
  { label: "Sep 15", value: 171 },
  { label: "Sep 16", value: 158 },
];

export function ChartFrame({
  title = "BTC / USD — 12d",
  insight = "lowest in 30d",
  points = MOCK_POINTS,
  unit = "$",
  status = "done",
  className,
  "data-testid": testId = "chart-frame",
}: ChartFrameProps) {
  const max = Math.max(...points.map((p) => p.value), 0);
  const minIndex = points.reduce(
    (acc, p, i) => (p.value < points[acc]!.value ? i : acc),
    0,
  );
  const [selectedIndex, setSelectedIndex] = useState<number | null>(minIndex);
  const selected = selectedIndex != null ? points[selectedIndex] : null;

  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex flex-col overflow-hidden rounded-frame",
        className,
      )}
      data-testid={testId}
      {...surfaceRoleAttributes("frame")}
    >
      <div className="flex flex-1 flex-col gap-2 px-3 py-2.5">
        {status === "error" ? (
          <div
            className="flex flex-col items-center gap-1.5 rounded-preview bg-danger-muted px-3 py-6 text-center"
            data-testid={`${testId}-error`}
          >
            <Icons.CircleAlert className="h-4 w-4 text-status-error" />
            <p className="text-canvas-sm text-secondary">
              Couldn&apos;t load the price trend.
            </p>
            <p className="text-canvas-xs text-muted">
              Check the data source and try again later.
            </p>
          </div>
        ) : status === "streaming" ? (
          <div data-testid={`${testId}-loading`}>
            <div className="mb-2 flex items-center gap-2">
              <Spinner className="h-3 w-3" />
              <span className="text-canvas-xs text-muted">
                Updating prices…
              </span>
            </div>
            <div className="flex h-28 items-end gap-1.5" aria-hidden="true">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="flex-1 animate-pulse rounded-sm bg-elevated/60"
                  style={{ height: `${28 + ((i * 37) % 64)}%` }}
                />
              ))}
            </div>
          </div>
        ) : points.length === 0 ? (
          <div
            className="flex flex-col items-center gap-1 rounded-preview bg-elevated/60 px-3 py-6 text-center"
            data-testid={`${testId}-empty`}
          >
            <Icons.BarChart2 className="h-4 w-4 text-muted" />
            <p className="text-canvas-sm text-secondary">No price data yet.</p>
            <p className="text-canvas-xs text-muted">
              Ask for a symbol to start tracking its trend.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span
                className="text-canvas-lg font-semibold tabular-nums text-primary"
                data-testid={`${testId}-value`}
              >
                {unit}
                {(selected ?? points[points.length - 1]!).value.toLocaleString()}
              </span>
              {insight && (
                <span
                  className="inline-flex items-center gap-1 rounded-pill bg-accent/18 px-2 py-0.5 text-canvas-2xs font-medium text-accent"
                  data-testid={`${testId}-insight`}
                >
                  <Icons.ArrowDown className="h-2.5 w-2.5" />
                  {insight}
                </span>
              )}
              {selected && (
                <span className="ml-auto text-canvas-2xs text-muted">
                  {selected.label}
                </span>
              )}
            </div>

            <div
              className="flex min-h-28 flex-1 items-end gap-1.5"
              role="group"
              aria-label={`${title} bar trend`}
            >
              {points.map((point, i) => {
                const isSelected = i === selectedIndex;
                return (
                  <button
                    key={`${point.label}-${i}`}
                    type="button"
                    onClick={() =>
                      setSelectedIndex(isSelected ? null : i)
                    }
                    aria-pressed={isSelected}
                    aria-label={`${point.label}: ${unit}${point.value}`}
                    data-testid={`${testId}-bar-${i}`}
                    className="group flex h-full flex-1 flex-col justify-end rounded-sm focus:outline-none"
                  >
                    <span
                      className={cn(
                        "w-full rounded-sm transition-colors",
                        isSelected
                          ? "bg-accent"
                          : "bg-accent/35 group-hover:bg-accent/60",
                      )}
                      style={{
                        height: `${max > 0 ? Math.max(6, (point.value / max) * 100) : 6}%`,
                      }}
                    />
                  </button>
                );
              })}
            </div>

            <div className="flex gap-1.5" aria-hidden="true">
              {points.map((point) => (
                <span
                  key={point.label}
                  className="flex-1 truncate text-center text-canvas-2xs text-muted"
                >
                  {point.label.replace(/^Sep\s/, "")}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export type { ChartFrameProps, ChartPoint, FrameStatus };
